import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import config from '../../config';
import { initializeMsalInstance, getMsalInitializationStatus, acquireGraphToken, acquireCustomApiToken } from '../../config/msalConfig';

export const createBaseQuery = (baseUrl: string, options?: { timeout?: number }) => {
	return fetchBaseQuery({
		baseUrl,
		timeout: options?.timeout,
		prepareHeaders: async headers => {
			try {
				// Initialize MSAL if not already done
				if (!getMsalInitializationStatus()) {
					await initializeMsalInstance();
				}

				// Get tokens
				const graphResult = await acquireGraphToken();
				const customResult = await acquireCustomApiToken();

				// Prefer custom API token for backend calls; attach Graph token separately when present
				const token: string | null = customResult?.accessToken ?? null;

				// Set appropriate headers
				headers.set('Ocp-Apim-Subscription-Key', config.APIM_KEY);
				if (token) headers.set('Authorization', `Bearer ${token}`);
				// Allow endpoints to opt-out of Graph header to avoid CORS preflight issues
				const omitGraphAuth = headers.get('X-Omit-Graph-Auth') === 'true';
				if (!omitGraphAuth && graphResult?.accessToken) {
					headers.set('Graph-Authorization', `Bearer ${graphResult.accessToken}`);
				} else if (omitGraphAuth) {
					headers.delete('Graph-Authorization');
				}

				return headers;
			} catch (error) {
				console.error('Error getting access token:', error);
				return headers;
			}
		},
	});
};

// Utility function to get auth headers for specific resource types
export const getAuthHeadersForResource = async (resource: 'graph' | 'custom') => {
	try {
		// Initialize MSAL if not already done
		if (!getMsalInitializationStatus()) {
			await initializeMsalInstance();
		}

		const result = resource === 'graph' ? await acquireGraphToken() : await acquireCustomApiToken();
		if (!result) return {};

		const headers: Record<string, string> = {};
		if (resource === 'graph') {
			headers['Authorization'] = `Bearer ${result.accessToken}`;
		} else {
			headers['Graph-Authorization'] = `Bearer ${result.accessToken}`;
		}

		return headers;
	} catch (error) {
		console.error(`Error getting auth headers for ${resource}:`, error);
		return {};
	}
};
